import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    QueryList,
    TemplateRef,
    ViewChild,
    ViewChildren,
} from '@angular/core';

import { Subject, takeUntil } from 'rxjs';

import {
    TwcPopoverComponent,
    TwcToolbarComponent,
    TwcToolbarOptionComponent,
    TwcTooltipSize,
    TwcTooltipTrigger,
} from '@topcon/twc-angular';
import { TwcPosition } from '@topcon/twc-angular/lib/common/utils/relative-positioning/relative-positioning.types';

import { OptionToolbar, OptionToolbarData, Toolbar } from '@mirage-projects/cw-commons';

import { NamedTemplateComponent } from '../named-template';
import { ToolbarService } from './toolbar.service';

/**
 * Component that renders a toolbar. With toolbar items and, child toolbars and child panels.
 */
@Component({
    selector: 'cw-toolbar',
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    standalone: false,
})
export class ToolbarComponent implements AfterViewChecked, AfterViewInit, OnDestroy {
    /**
     * The toolbar definition object.
     */
    @Input() toolbarDefinition!: Toolbar;

    /**
     * Event emitted when a toolbar option is selected.
     */
    @Output() selectOption: EventEmitter<OptionToolbarData> = new EventEmitter();

    @ContentChildren(NamedTemplateComponent) panelItems!: QueryList<NamedTemplateComponent>;
    @ViewChildren(TwcPopoverComponent, { read: ElementRef }) popoversRef!: QueryList<ElementRef>;
    @ViewChildren(TwcPopoverComponent) popoverComponents!: QueryList<TwcPopoverComponent>;
    @ViewChild('twcToolbar') twcToolbar!: TwcToolbarComponent;

    defaultPanelTrigger: TwcTooltipTrigger = ['click', 'focus', 'manual'];
    defaultPanelWidth: TwcTooltipSize = 'm';
    defaultPanelPlacement: TwcPosition = 'bottom';

    private destroy$ = new Subject<void>();

    constructor(
        private cdr: ChangeDetectorRef,
        private toolbarService: ToolbarService
    ) {}

    ngAfterViewInit(): void {
        this.startVisibilityObserver();
        this.startClosePopoversObserver();
        this.toolbarService.registerToolbar(this.twcToolbar);
    }

    ngAfterViewChecked(): void {
        this.cdr.detectChanges();
    }

    ngOnDestroy(): void {
        this.toolbarService.removeRegisterToolbar(this.twcToolbar);
        this.destroy$.next();
        this.destroy$.complete();
    }

    onSelectOption(option: TwcToolbarOptionComponent): void {
        this.selectOption.emit(option.data as OptionToolbarData);
        this.toolbarService.selectOptionAndShowToolPanel(option.data as OptionToolbarData);
    }

    onCloseAllPopovers(): void {
        this.popoverComponents.forEach((popoverComponent) => {
            const isVisible = (popoverComponent as any).tooltipElement;
            if (!isVisible) {
                return;
            }

            popoverComponent.close();
            popoverComponent.hide();
            popoverComponent.onTrigger.next(false);
        });
    }

    getPanelByName(name: string): TemplateRef<any> | null {
        return this.panelItems.find((panel) => panel.name === name)?.template ?? null;
    }

    updatePanelStatus(panelName: string, status: boolean): void {
        this.toolbarService.emitStatusChange(panelName, status);
    }

    /** This function collpases the others toolbars. It is used to collapse the expanded toolbars when you click on one */
    collapseOthersToolbars(forceAll = false): void {
        this.toolbarService.collapseOthersToolbars(this.twcToolbar, forceAll);
    }

    openPanel(option: OptionToolbar): void {
        if (option.disabled) {
            return;
        }
        const panelName = option.childPanel?.name;
        if (!panelName) {
            return;
        }
        this.toolbarService.emitStatusChange(panelName, true);
        const index = this.popoversRef.toArray().findIndex((popover) => popover.nativeElement.getAttribute('data-name') === panelName);
        const popoverComponent = this.popoverComponents.get(index);
        const isVisible = (popoverComponent as any).tooltipElement;

        if (!popoverComponent) {
            return;
        }
        if (!isVisible) {
            setTimeout(() => {
                popoverComponent.triggerOn();
                popoverComponent.show();
                popoverComponent.onTrigger.next(true);
            }, 0);
        } else {
            popoverComponent.hide();
            popoverComponent.onTrigger.next(false);
        }
    }

    private startVisibilityObserver(): void {
        this.toolbarService.panelVisibility$?.pipe(takeUntil(this.destroy$)).subscribe(([name, visibility]) => {
            const popoverIndex = [...this.popoversRef].findIndex((popover) => popover.nativeElement.getAttribute('data-name') === name);

            const popoverComponent = this.popoverComponents.get(popoverIndex);

            if (!popoverComponent) {
                return;
            }

            const isShowing = visibility === 'show';
            const isHiding = visibility === 'hide';
            const isRedrawing = visibility === 'redraw';

            if (isHiding || isRedrawing) {
                popoverComponent.hide();
            }

            if (isShowing || isRedrawing) {
                popoverComponent.triggerOn();
                popoverComponent.show();
            }

            if (isShowing || isHiding) {
                popoverComponent.onTrigger.next(isShowing);
            }
        });
    }

    private startClosePopoversObserver(): void {
        this.toolbarService.closePopovers$?.pipe(takeUntil(this.destroy$)).subscribe(() => this.onCloseAllPopovers());
    }
}
